import { Popover } from '@headlessui/react';
import { ChevronDownIcon, SearchIcon } from '@heroicons/react/outline';
import { debounce } from 'lodash';
import { useEffect, useState } from 'react';
import classNames from 'utils/classNames';
import InputPrepend from './Forms/InputPrepend';
import { strUnderscoreToSpace } from 'utils/formatters';

const AddonsListSelect = ({
  addons,
  selectedId,
  onChange,
  disabled,
  position = 'left-1/2 -translate-x-1/2 transform',
  width = 'w-64',
  keyField = 'addon_code',
  textColor = 'text-grayscale-700',
  textSize = 'text-sm',
  containerWidth = 'w-full max-w-sm',
}) => {
  const [filteredAddons, setFilteredAddons] = useState([]);
  const [selectedAddon, setSelectedAddon] = useState(null);

  useEffect(() => {
    setFilteredAddons(addons);
  }, [addons]);

  useEffect(() => {
    if (selectedId && addons) {
      setSelectedAddon(addons.find((a) => a[keyField] === selectedId));
    } else {
      setSelectedAddon(null);
    }
  }, [selectedId, addons]);

  const onSearch = (e) => {
    const search = e.target.value.toLowerCase();

    let f = addons.filter((a) => a.name.toLowerCase().includes(search));
    setFilteredAddons(f);
  };
  const onDebouncedSearch = debounce((e) => {
    onSearch(e);
  }, 500);

  return (
    <div className={classNames(containerWidth)}>
      <Popover className="relative">
        {({ open }) => (
          <>
            <Popover.Button
              disabled={disabled}
              className={classNames(
                open ? '' : 'text-opacity-90',
                textSize,
                'group flex w-full items-center rounded-sm bg-orange-700 py-0 font-medium hover:text-opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75'
              )}
            >
              <label className="flex items-center justify-between w-full px-2 py-3 cursor-pointer rounded-lg focus:outline-none sm:text-sm appearance-none border-white bg-gray-50 placeholder-gray-400 hover:ring-gray-200 hover:border-gray-200 hover:border-gray-200">
                {selectedAddon ? (
                  <span className="flex space-x-2">
                    <span>{selectedAddon.name}</span>
                    <span
                      className={classNames(
                        selectedAddon.type === 'recurring'
                          ? 'bg-red-100 text-red-600'
                          : 'bg-green-100 text-green-600',
                        'capitalize text-1xs tracking-wider rounded-sm px-2'
                      )}
                    >
                      {strUnderscoreToSpace(selectedAddon.type)}
                    </span>
                  </span>
                ) : (
                  <span>Select Service</span>
                )}

                <ChevronDownIcon className="ml-1 w-4 h-4 text-gray-400" />
              </label>
            </Popover.Button>

            <Popover.Panel
              className={classNames(
                position,
                width,
                'absolute z-20 min-h-full'
              )}
            >
              {({ close }) => (
                <div className="overflow-hidden rounded-lg shadow-lg ring-1 ring-black ring-opacity-5">
                  <div className="relative bg-white">
                    <div className="border-b">
                      <InputPrepend
                        name="search"
                        onChange={(e) => onDebouncedSearch(e)}
                        type="text"
                        placeholder="Search..."
                        prependText={<SearchIcon className="w-4 h-4" />}
                        border="border-none"
                        rounded="rounded-none"
                        classes="text-base"
                        autoFocus
                      />
                    </div>
                    <div className="overflow-y-auto h-72 py-4 px-2 text-left">
                      {filteredAddons.length > 0 ? (
                        filteredAddons.map((a) => {
                          return (
                            <button
                              className="flex items-center justify-between w-full text-left px-2 py-1 space-x-3 text-sm hover:bg-gray-100 rounded-md focus:ring-gray-300 focus:border-gray-200"
                              key={a[keyField]}
                              value={a[keyField]}
                              onClick={() => {
                                onChange(a);
                                close();
                              }}
                            >
                              <span>{a.name}</span>
                              <span
                                className={classNames(
                                  a.type === 'recurring'
                                    ? 'bg-red-100 text-red-600'
                                    : 'bg-green-100 text-green-600',
                                  'capitalize text-1xs tracking-wider rounded-sm px-2'
                                )}
                              >
                                {strUnderscoreToSpace(a.type)}
                              </span>
                            </button>
                          );
                        })
                      ) : (
                        <div className="w-full text-xs text-gray-700 py-2 px-3">
                          No addons found.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </Popover.Panel>
          </>
        )}
      </Popover>
    </div>
  );
};

export default AddonsListSelect;
